import React, { useState, useRef, useEffect, useLayoutEffect } from 'react'
import styled from 'styled-components'
import { LayoutGrid, Table, ChevronDown, Filter, Search, X, Building2, Wrench } from 'lucide-react'
import { DateFilterField, usePipelineContext } from '../PipelineContext'
import UserAvatar from '../../../../components/UserAvatar'

const SelectWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const SelectButton = styled.button<{ $isOpen?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: #F5F5F5;
  border: 1px solid #E0E0E0;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #333;
  min-width: 120px;
  justify-content: space-between;

  &:hover {
    background-color: #EBEBEB;
  }

  svg:last-child {
    transition: transform 0.2s;
    transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`

const Dropdown = styled.div<{ $width?: string; $flipH?: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  ${props => props.$flipH ? 'right: 0;' : 'left: 0;'}
  background: white;
  border: 1px solid #E0E0E0;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: ${props => props.$width || '180px'};
  max-width: calc(100vw - 24px);
  overflow: hidden;
`

const DropdownItem = styled.div<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: ${props => props.$isActive ? '#0090D3' : '#333'};
  background-color: ${props => props.$isActive ? '#F0F9FF' : 'transparent'};

  &:hover {
    background-color: #F5F5F5;
  }
`

export const OutlineButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: transparent;
  border: 1px solid #0090D3;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1;
  color: #0090D3;
  box-sizing: border-box;
  white-space: nowrap;

  &:hover {
    background-color: #E6F4FB;
  }
`

const FilterButton = styled(OutlineButton)<{ $hasFilters?: boolean }>`
  background-color: ${props => props.$hasFilters ? '#0090D3' : 'transparent'};
  border-color: #0090D3;
  color: ${props => props.$hasFilters ? '#fff' : '#0090D3'};

  &:hover {
    background-color: ${props => props.$hasFilters ? '#007bb5' : '#E6F4FB'};
  }
`

const FilterDropdownContainer = styled.div<{ $flipH?: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  ${props => props.$flipH ? 'left: 0;' : 'right: 0;'}
  background: white;
  border: 1px solid #E0E0E0;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  width: 280px;
  max-width: calc(100vw - 24px);
  max-height: 400px;
  overflow-y: auto;
`

const FilterSection = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #E0E0E0;

  &:last-child {
    border-bottom: none;
  }
`

const FilterSectionTitle = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  color: #333;
  margin-bottom: 0.75rem;
`

const SearchInput = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background-color: #F5F5F5;
  border-radius: 0.5rem;

  input {
    border: none;
    background: transparent;
    outline: none;
    flex: 1;
    font-size: 0.875rem;
  }
`

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  cursor: pointer;
  font-size: 0.875rem;
  color: #333;

  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`

const UserItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  cursor: pointer;
  font-size: 0.875rem;
  color: #333;

  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`


const TagItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  cursor: pointer;
`

const TagBadge = styled.span<{ $color?: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  background-color: ${props => props.$color || '#E0E0E0'};
  color: ${props => props.$color ? '#fff' : '#333'};
`

const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const OrderLabel = styled.span`
  font-size: 0.875rem;
  color: #666;
`

const DateFieldSelect = styled.select`
  width: 100%;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #E0E0E0;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: #333;
  background: #fff;
  margin-bottom: 0.75rem;

  &:focus {
    outline: none;
    border-color: #0090D3;
  }
`

const DateRangeRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`

const DateRangeLabel = styled.span`
  font-size: 0.75rem;
  color: #666;
  min-width: 28px;
`

const DateInput = styled.input`
  flex: 1;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #E0E0E0;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: #333;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #0090D3;
  }
`

const ClearPeriodButton = styled.button`
  margin-top: 0.5rem;
  background: none;
  border: none;
  color: #0090D3;
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`

interface ViewModeOption {
  value: string
  label: string
  icon?: React.ReactNode
}

interface ViewModeSelectorProps {
  value: string
  onChange: (value: any) => void
  boardLabel?: string
  tableLabel?: string
  options?: ViewModeOption[]
}

export const ViewModeSelector: React.FC<ViewModeSelectorProps> = ({
  value,
  onChange,
  boardLabel = 'Board',
  tableLabel = 'Tabela',
  options,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [flipH, setFlipH] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useLayoutEffect(() => {
    if (isOpen && ref.current) {
      const rect = ref.current.getBoundingClientRect()
      const wouldOverflowRight = rect.left + 180 > window.innerWidth - 12
      setFlipH(wouldOverflowRight)
    }
  }, [isOpen])

  const resolvedOptions: ViewModeOption[] = options || [
    { value: 'board', label: boardLabel, icon: <LayoutGrid size={16} /> },
    { value: 'table', label: tableLabel, icon: <Table size={16} /> },
  ]

  const activeOption = resolvedOptions.find(o => o.value === value) || resolvedOptions[0]

  return (
    <SelectWrapper ref={ref}>
      <SelectButton onClick={() => setIsOpen(!isOpen)} $isOpen={isOpen}>
        {activeOption?.icon}
        {activeOption?.label}
        <ChevronDown size={16} />
      </SelectButton>
      {isOpen && (
        <Dropdown $flipH={flipH}>
          {resolvedOptions.map(option => (
            <DropdownItem
              key={option.value}
              $isActive={value === option.value}
              onClick={() => { onChange(option.value); setIsOpen(false) }}
            >
              {option.icon}
              {option.label}
            </DropdownItem>
          ))}
        </Dropdown>
      )}
    </SelectWrapper>
  )
}

export interface SortOption {
  value: string
  label: string
}

const defaultSortOptions: SortOption[] = [
  { value: 'phase', label: 'Etapa' },
  { value: 'name_asc', label: 'Nome (crescente)' },
  { value: 'name_desc', label: 'Nome (decrescente)' },
  { value: 'recent', label: 'Mais recente' },
  { value: 'oldest', label: 'Menos recente' },
  { value: 'urgency', label: 'Urgência' },
  { value: 'deadline', label: 'Próximo de vencer' },
]

interface SortBySelectorProps {
  value: string
  onChange: (value: string) => void
  options?: SortOption[]
}

export const SortBySelector: React.FC<SortBySelectorProps> = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [flipH, setFlipH] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useLayoutEffect(() => {
    if (isOpen && ref.current) {
      const rect = ref.current.getBoundingClientRect()
      const wouldOverflowRight = rect.left + 200 > window.innerWidth - 12
      setFlipH(wouldOverflowRight)
    }
  }, [isOpen])

  const effectiveOptions = options || defaultSortOptions

  const currentLabel = value
    ? (effectiveOptions.find(o => o.value === value)?.label || 'Selecionar')
    : 'Selecionar'

  return (
    <SelectWrapper ref={ref}>
      <OrderLabel>Ordenar por</OrderLabel>
      <SelectButton onClick={() => setIsOpen(!isOpen)} $isOpen={isOpen} style={{ marginLeft: '0.5rem' }}>
        {currentLabel}
        <ChevronDown size={16} />
      </SelectButton>
      {isOpen && (
        <Dropdown $width="200px" $flipH={flipH}>
          {effectiveOptions.map(option => (
            <DropdownItem 
              key={option.value} 
              $isActive={value === option.value}
              onClick={() => { onChange(option.value); setIsOpen(false) }}
            >
              {option.label}
            </DropdownItem>
          ))}
        </Dropdown>
      )}
    </SelectWrapper>
  )
}

interface FilterDropdownProps {
  users?: { id: number; name: string; picture?: string }[]
  tags?: { id: number; name: string; color?: string }[]
}

export const FilterDropdown: React.FC<FilterDropdownProps> = () => {
  const { filters, setFilters, availableUsers, availableTags, isModuleView, moduleType } = usePipelineContext()
  const isProductionFunnel = isModuleView && moduleType === 'production'
  const isProductDevelopmentFunnel = isModuleView && moduleType === 'product_development'
  const [isOpen, setIsOpen] = useState(false)
  const [flipH, setFlipH] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useLayoutEffect(() => {
    if (isOpen && ref.current) {
      const rect = ref.current.getBoundingClientRect()
      const wouldOverflowLeft = rect.right - 280 < 12
      setFlipH(wouldOverflowLeft)
    }
  }, [isOpen])

  const hasActiveFilters = !!(filters.keyword || filters.urgency ||
    filters.dueDate.notExpired || filters.dueDate.nearExpiry || filters.dueDate.expired ||
    filters.dueDate.specificDate || filters.dueDate.from || filters.dueDate.to ||
    filters.dateFilter.from || filters.dateFilter.to ||
    filters.assignedUsers.length > 0 || filters.tags.length > 0 ||
    filters.productDevelopment.collectionName || filters.productDevelopment.familyName ||
    filters.productDevelopment.productCode)

  const clearDueDateCheckboxes = () => ({
    notExpired: false,
    nearExpiry: false,
    expired: false,
  })

  const clearDueDateDateFields = () => ({
    specificDate: '',
    from: '',
    to: '',
  })

  const toggleUser = (userId: number) => {
    setFilters(prev => ({
      ...prev,
      assignedUsers: prev.assignedUsers.includes(userId)
        ? prev.assignedUsers.filter(id => id !== userId)
        : [...prev.assignedUsers, userId]
    }))
  }

  const toggleTag = (tagId: number) => {
    setFilters(prev => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter(id => id !== tagId)
        : [...prev.tags, tagId]
    }))
  }

  console.debug('[FilterDropdown] Available users:', availableUsers)
  console.debug('[FilterDropdown] Available tags:', availableTags)

  return (
    <SelectWrapper ref={ref} style={{ position: 'relative' }}>
      <FilterButton onClick={() => setIsOpen(!isOpen)} $hasFilters={hasActiveFilters}>
        <Filter size={16} /> Filtrar
      </FilterButton>
      {isOpen && (
        <FilterDropdownContainer $flipH={flipH}>
          <FilterSection>
            <FilterSectionTitle>Filtrar</FilterSectionTitle>
            <FilterSectionTitle style={{ fontWeight: 400, marginBottom: '0.5rem' }}>Palavra-chave</FilterSectionTitle>
            <SearchInput>
              <Search size={16} color="#999" />
              <input 
                type="text" 
                placeholder="Buscar" 
                value={filters.keyword}
                onChange={(e) => setFilters(prev => ({ ...prev, keyword: e.target.value }))}
              />
              {filters.keyword && (
                <X 
                  size={16} 
                  color="#999" 
                  style={{ cursor: 'pointer' }}
                  onClick={() => setFilters(prev => ({ ...prev, keyword: '' }))}
                />
              )}
            </SearchInput>
          </FilterSection>

          <FilterSection>
            <FilterSectionTitle>Urgência</FilterSectionTitle>
            <CheckboxItem>
              <input 
                type="checkbox" 
                checked={filters.urgency}
                onChange={(e) => setFilters(prev => ({ ...prev, urgency: e.target.checked }))}
              />
              Urgente
            </CheckboxItem>
          </FilterSection>

          <FilterSection>
            <FilterSectionTitle>Data de vencimento</FilterSectionTitle>
            <CheckboxItem>
              <input 
                type="checkbox" 
                checked={filters.dueDate.notExpired}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  dueDate: {
                    ...prev.dueDate,
                    ...clearDueDateDateFields(),
                    notExpired: e.target.checked,
                  },
                }))}
              />
              Não vencido
            </CheckboxItem>
            <CheckboxItem>
              <input 
                type="checkbox" 
                checked={filters.dueDate.nearExpiry}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  dueDate: {
                    ...prev.dueDate,
                    ...clearDueDateDateFields(),
                    nearExpiry: e.target.checked,
                  },
                }))}
              />
              Próximo ao vencimento
            </CheckboxItem>
            <CheckboxItem>
              <input 
                type="checkbox" 
                checked={filters.dueDate.expired}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  dueDate: {
                    ...prev.dueDate,
                    ...clearDueDateDateFields(),
                    expired: e.target.checked,
                  },
                }))}
              />
              Vencido
            </CheckboxItem>
            <FilterSectionTitle style={{ fontWeight: 400, marginTop: '0.75rem', marginBottom: '0.5rem' }}>
              Data específica
            </FilterSectionTitle>
            <DateInput
              type="date"
              value={filters.dueDate.specificDate}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                dueDate: {
                  ...prev.dueDate,
                  ...clearDueDateCheckboxes(),
                  specificDate: e.target.value,
                  from: '',
                  to: '',
                },
              }))}
            />
            <FilterSectionTitle style={{ fontWeight: 400, marginTop: '0.75rem', marginBottom: '0.5rem' }}>
              Intervalo
            </FilterSectionTitle>
            <DateRangeRow>
              <DateRangeLabel>De</DateRangeLabel>
              <DateInput
                type="date"
                value={filters.dueDate.from}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  dueDate: {
                    ...prev.dueDate,
                    ...clearDueDateCheckboxes(),
                    specificDate: '',
                    from: e.target.value,
                  },
                }))}
              />
            </DateRangeRow>
            <DateRangeRow style={{ marginTop: '0.5rem' }}>
              <DateRangeLabel>Até</DateRangeLabel>
              <DateInput
                type="date"
                value={filters.dueDate.to}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  dueDate: {
                    ...prev.dueDate,
                    ...clearDueDateCheckboxes(),
                    specificDate: '',
                    to: e.target.value,
                  },
                }))}
              />
            </DateRangeRow>
            {(filters.dueDate.specificDate || filters.dueDate.from || filters.dueDate.to) && (
              <ClearPeriodButton
                type="button"
                onClick={() => setFilters(prev => ({
                  ...prev,
                  dueDate: {
                    ...prev.dueDate,
                    ...clearDueDateDateFields(),
                  },
                }))}
              >
                Limpar datas
              </ClearPeriodButton>
            )}
          </FilterSection>

          {isProductionFunnel && (
            <FilterSection>
              <FilterSectionTitle>Período</FilterSectionTitle>
              <DateFieldSelect
                value={filters.dateFilter.field}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  dateFilter: { ...prev.dateFilter, field: e.target.value as DateFilterField },
                }))}
              >
                <option value="deadline">Data de vencimento</option>
                <option value="opStart">Data de criação da OP</option>
              </DateFieldSelect>
              <DateRangeRow>
                <DateRangeLabel>De</DateRangeLabel>
                <DateInput
                  type="date"
                  value={filters.dateFilter.from}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    dateFilter: { ...prev.dateFilter, from: e.target.value },
                  }))}
                />
              </DateRangeRow>
              <DateRangeRow style={{ marginTop: '0.5rem' }}>
                <DateRangeLabel>Até</DateRangeLabel>
                <DateInput
                  type="date"
                  value={filters.dateFilter.to}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    dateFilter: { ...prev.dateFilter, to: e.target.value },
                  }))}
                />
              </DateRangeRow>
              {(filters.dateFilter.from || filters.dateFilter.to) && (
                <ClearPeriodButton
                  type="button"
                  onClick={() => setFilters(prev => ({
                    ...prev,
                    dateFilter: { ...prev.dateFilter, from: '', to: '' },
                  }))}
                >
                  Limpar período
                </ClearPeriodButton>
              )}
            </FilterSection>
          )}

          {isProductDevelopmentFunnel && (
            <FilterSection>
              <FilterSectionTitle>Produto</FilterSectionTitle>
              <FilterSectionTitle style={{ fontWeight: 400, marginBottom: '0.5rem' }}>Código</FilterSectionTitle>
              <SearchInput>
                <Search size={16} color="#999" />
                <input
                  type="text"
                  placeholder="Buscar por código"
                  value={filters.productDevelopment.productCode}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    productDevelopment: { ...prev.productDevelopment, productCode: e.target.value },
                  }))}
                />
                {filters.productDevelopment.productCode && (
                  <X
                    size={16}
                    color="#999"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setFilters(prev => ({ ...prev, productDevelopment: { ...prev.productDevelopment, productCode: '' } }))}
                  />
                )}
              </SearchInput>
              <FilterSectionTitle style={{ fontWeight: 400, margin: '0.75rem 0 0.5rem' }}>Coleção</FilterSectionTitle>
              <SearchInput>
                <Search size={16} color="#999" />
                <input
                  type="text"
                  placeholder="Buscar por coleção"
                  value={filters.productDevelopment.collectionName}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    productDevelopment: { ...prev.productDevelopment, collectionName: e.target.value },
                  }))}
                />
                {filters.productDevelopment.collectionName && (
                  <X
                    size={16}
                    color="#999"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setFilters(prev => ({ ...prev, productDevelopment: { ...prev.productDevelopment, collectionName: '' } }))}
                  />
                )}
              </SearchInput>
              <FilterSectionTitle style={{ fontWeight: 400, margin: '0.75rem 0 0.5rem' }}>Família</FilterSectionTitle>
              <SearchInput>
                <Search size={16} color="#999" />
                <input
                  type="text"
                  placeholder="Buscar por família"
                  value={filters.productDevelopment.familyName}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    productDevelopment: { ...prev.productDevelopment, familyName: e.target.value },
                  }))}
                />
                {filters.productDevelopment.familyName && (
                  <X
                    size={16}
                    color="#999"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setFilters(prev => ({ ...prev, productDevelopment: { ...prev.productDevelopment, familyName: '' } }))}
                  />
                )}
              </SearchInput>
            </FilterSection>
          )}

          {availableUsers.length > 0 && (
            <FilterSection>
              <FilterSectionTitle>Atribuição</FilterSectionTitle>
              {availableUsers.map(user => (
                <UserItem key={user.id}>
                  <input 
                    type="checkbox" 
                    checked={filters.assignedUsers.includes(user.id)}
                    onChange={() => toggleUser(user.id)}
                  />
                  <UserAvatar
                    src={user.picture}
                    name={user.name}
                    size={24}
                  />
                  {user.name}
                </UserItem>
              ))}
            </FilterSection>
          )}

          {availableTags.length > 0 && (
            <FilterSection>
              <FilterSectionTitle>Tags</FilterSectionTitle>
              {availableTags.map(tag => (
                <TagItem key={tag.id}>
                  <input 
                    type="checkbox" 
                    checked={filters.tags.includes(tag.id)}
                    onChange={() => toggleTag(tag.id)}
                  />
                  <TagBadge $color={tag.color}>{tag.name}</TagBadge>
                </TagItem>
              ))}
            </FilterSection>
          )}
        </FilterDropdownContainer>
      )}
    </SelectWrapper>
  )
}

const AppliedFiltersBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px 12px;
  padding: 0.6rem 1.5rem;
  margin-bottom: 0.5rem;
  background-color: #F8FAFC;
  border-bottom: 1px solid #E2E8F0;
  flex-wrap: wrap;
  min-height: 36px;
`

const FilterChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.6rem;
  background-color: #E0F2FE;
  border: 1px solid #BAE6FD;
  border-radius: 1rem;
  font-size: 0.75rem;
  color: #0369A1;
  white-space: nowrap;

  svg {
    cursor: pointer;
    flex-shrink: 0;
    &:hover {
      color: #DC2626;
    }
  }
`

const ClearAllButton = styled.button`
  background: none;
  border: none;
  color: #64748B;
  font-size: 0.75rem;
  cursor: pointer;
  text-decoration: underline;
  padding: 0.25rem 0.4rem;
  white-space: nowrap;

  &:hover {
    color: #DC2626;
  }
`

export const PipelineAppliedFilters: React.FC = () => {
  const { filters, setFilters, availableUsers, availableTags } = usePipelineContext()

  const hasActiveFilters = !!(filters.keyword || filters.urgency ||
    filters.dueDate.notExpired || filters.dueDate.nearExpiry || filters.dueDate.expired ||
    filters.dueDate.specificDate || filters.dueDate.from || filters.dueDate.to ||
    filters.dateFilter.from || filters.dateFilter.to ||
    filters.assignedUsers.length > 0 || filters.tags.length > 0 ||
    filters.productDevelopment.collectionName || filters.productDevelopment.familyName ||
    filters.productDevelopment.productCode)

  if (!hasActiveFilters) return null

  const chips: { label: string; onRemove: () => void }[] = []

  if (filters.keyword) {
    chips.push({
      label: `Palavra-chave: ${filters.keyword}`,
      onRemove: () => setFilters(prev => ({ ...prev, keyword: '' }))
    })
  }

  if (filters.urgency) {
    chips.push({
      label: 'Urgência',
      onRemove: () => setFilters(prev => ({ ...prev, urgency: false }))
    })
  }

  if (filters.dueDate.notExpired) {
    chips.push({
      label: 'Prazo: Não vencido',
      onRemove: () => setFilters(prev => ({ ...prev, dueDate: { ...prev.dueDate, notExpired: false } }))
    })
  }

  if (filters.dueDate.nearExpiry) {
    chips.push({
      label: 'Prazo: Próximo ao vencimento',
      onRemove: () => setFilters(prev => ({ ...prev, dueDate: { ...prev.dueDate, nearExpiry: false } }))
    })
  }

  if (filters.dueDate.expired) {
    chips.push({
      label: 'Prazo: Vencido',
      onRemove: () => setFilters(prev => ({ ...prev, dueDate: { ...prev.dueDate, expired: false } }))
    })
  }

  if (filters.dueDate.specificDate) {
    chips.push({
      label: `Data: ${filters.dueDate.specificDate}`,
      onRemove: () => setFilters(prev => ({ ...prev, dueDate: { ...prev.dueDate, specificDate: '' } }))
    })
  }

  if (filters.dueDate.from || filters.dueDate.to) {
    chips.push({
      label: `Prazo: ${filters.dueDate.from || '...'} até ${filters.dueDate.to || '...'}`,
      onRemove: () => setFilters(prev => ({ ...prev, dueDate: { ...prev.dueDate, from: '', to: '' } }))
    })
  }

  if (filters.dateFilter.from || filters.dateFilter.to) {
    const fieldLabel = filters.dateFilter.field === 'deadline' ? 'Prazo' : 'Início OP'
    chips.push({
      label: `${fieldLabel}: ${filters.dateFilter.from || '...'} até ${filters.dateFilter.to || '...'}`,
      onRemove: () => setFilters(prev => ({ ...prev, dateFilter: { ...prev.dateFilter, from: '', to: '' } }))
    })
  }

  filters.assignedUsers.forEach(userId => {
    const user = availableUsers.find(u => u.id === userId)
    chips.push({
      label: `Usuário: ${user?.name || `#${userId}`}`,
      onRemove: () => setFilters(prev => ({ ...prev, assignedUsers: prev.assignedUsers.filter(id => id !== userId) }))
    })
  })

  filters.tags.forEach(tagId => {
    const tag = availableTags.find(t => t.id === tagId)
    chips.push({
      label: `Tag: ${tag?.name || `#${tagId}`}`,
      onRemove: () => setFilters(prev => ({ ...prev, tags: prev.tags.filter(id => id !== tagId) }))
    })
  })

  if (filters.productDevelopment.productCode) {
    chips.push({
      label: `Código: ${filters.productDevelopment.productCode}`,
      onRemove: () => setFilters(prev => ({ ...prev, productDevelopment: { ...prev.productDevelopment, productCode: '' } }))
    })
  }

  if (filters.productDevelopment.collectionName) {
    chips.push({
      label: `Coleção: ${filters.productDevelopment.collectionName}`,
      onRemove: () => setFilters(prev => ({ ...prev, productDevelopment: { ...prev.productDevelopment, collectionName: '' } }))
    })
  }

  if (filters.productDevelopment.familyName) {
    chips.push({
      label: `Família: ${filters.productDevelopment.familyName}`,
      onRemove: () => setFilters(prev => ({ ...prev, productDevelopment: { ...prev.productDevelopment, familyName: '' } }))
    })
  }

  const handleClearAll = () => {
    setFilters({
      keyword: '',
      urgency: false,
      dueDate: { notExpired: false, nearExpiry: false, expired: false, specificDate: '', from: '', to: '' },
      dateFilter: { field: filters.dateFilter.field, from: '', to: '' },
      assignedUsers: [],
      tags: [],
      productDevelopment: { collectionName: '', familyName: '', productCode: '' },
    })
  }

  return (
    <AppliedFiltersBar>
      {chips.map((chip, index) => (
        <FilterChip key={index}>
          {chip.label}
          <X size={12} onClick={chip.onRemove} />
        </FilterChip>
      ))}
      <ClearAllButton onClick={handleClearAll}>Limpar todos</ClearAllButton>
    </AppliedFiltersBar>
  )
}

export { ControlsContainer }
